import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api-client';

interface Issue {
  id: number;
  title: string;
  description?: string;
  category?: string;
  root_cause?: string;
  resolution?: string;
  status: string;
  times_occurred: number;
  first_seen_at: string;
  last_seen_at: string;
  project_id?: string;
}

interface DuplicateMatch {
  id: number;
  title: string;
  similarity: number;
  times_occurred: number;
  status: string;
}

export function IssuesPage() {
  const [searchTitle, setSearchTitle] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingIssue, setEditingIssue] = useState<Issue | null>(null);
  const [duplicateResults, setDuplicateResults] = useState<DuplicateMatch[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importProject, setImportProject] = useState('');
  const [importResult, setImportResult] = useState<any>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await apiClient.get('/projects');
      return res.data;
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!importFile) return;
      const formData = new FormData();
      formData.append('file', importFile);
      if (importProject) formData.append('project_key', importProject);
      const res = await apiClient.post('/issues/import', formData);
      return res.data;
    },
    onSuccess: (data) => {
      setImportResult(data);
      setImportFile(null);
      queryClient.invalidateQueries({ queryKey: ['issues'] });
    },
  });
  const queryClient = useQueryClient();

  const { data: issues = [], isLoading } = useQuery({
    queryKey: ['issues', statusFilter],
    queryFn: async () => {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await apiClient.get(`/issues${params}`);
      return res.data;
    },
  });

  const checkDuplicatesMutation = useMutation({
    mutationFn: async (title: string) => {
      const res = await apiClient.post('/issues/check', { title });
      return res.data;
    },
    onSuccess: (data) => {
      setDuplicateResults(data.matches || []);
    },
  });

  const updateIssueMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; root_cause?: string; resolution?: string; status?: string }) => {
      const res = await apiClient.patch(`/issues/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setEditingIssue(null);
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'investigating': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOccurrenceColor = (count: number) => {
    if (count >= 5) return 'text-red-600 font-bold';
    if (count >= 3) return 'text-orange-600 font-semibold';
    return 'text-gray-600';
  };

  const openIssues = issues.filter((i: Issue) => i.status === 'open').length;
  const repeatedIssues = issues.filter((i: Issue) => i.times_occurred > 1).length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Issue Register</h1>
          <p className="text-gray-500 text-sm mt-1">Track recurring issues and identify patterns</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{issues.length}</p>
          <p className="text-sm text-gray-500">Total Issues</p>
        </div>
        <div className="bg-white border border-red-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{openIssues}</p>
          <p className="text-sm text-gray-500">Open</p>
        </div>
        <div className="bg-white border border-orange-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{repeatedIssues}</p>
          <p className="text-sm text-gray-500">Repeated Issues</p>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">
            {issues.filter((i: Issue) => i.status === 'resolved').length}
          </p>
          <p className="text-sm text-gray-500">Resolved</p>
        </div>
      </div>

      {/* Import Section */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowImport(!showImport)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          {showImport ? 'Hide Import' : '📥 Import CSV'}
        </button>
      </div>

      {showImport && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Import Issues from CSV</h2>
          <p className="text-xs text-gray-500 mb-3">
            Required column: <strong>title</strong>. Optional: description, category, project_key, status, root_cause, resolution, times_occurred, first_seen_at (YYYY-MM-DD), last_seen_at
          </p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Default Project</label>
              <select
                value={importProject}
                onChange={(e) => setImportProject(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="">From CSV column</option>
                {projects.map((p: any) => (
                  <option key={p.id} value={p.key}>{p.key} — {p.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => importMutation.mutate()}
              disabled={!importFile || importMutation.isPending}
              className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700" disabled:opacity-50
            >
              {importMutation.isPending ? 'Importing...' : 'Import'}
            </button>
          </div>
          {importResult && (
            <div className="mt-3 p-3 bg-white rounded border border-gray-200 text-sm">
              <p className="font-medium text-gray-900">
                ✅ {importResult.imported} imported, {importResult.duplicates_found} duplicates merged, {importResult.skipped} skipped
              </p>
              {importResult.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  {importResult.errors.map((e: string, i: number) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duplicate Checker */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h2 className="text-sm font-semibold text-blue-800 mb-2">🔍 Check for Duplicates</h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            placeholder="Enter issue title to check for similar existing issues..."
            className="flex-1 border border-blue-300 rounded-lg px-3 py-2 text-sm"
          />
          <button
            onClick={() => checkDuplicatesMutation.mutate(searchTitle)}
            disabled={!searchTitle.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
          >
            Check
          </button>
        </div>
        {duplicateResults.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-sm font-medium text-orange-800">⚠️ Potential duplicates found:</p>
            {duplicateResults.map((match) => (
              <div key={match.id} className="bg-white rounded p-3 flex justify-between items-center">
                <div>
                  <span className="font-medium text-gray-900">{match.title}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    ({Math.round(match.similarity * 100)}% match, occurred {match.times_occurred}x)
                  </span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(match.status)}`}>
                  {match.status}
                </span>
              </div>
            ))}
          </div>
        )}
        {duplicateResults.length === 0 && checkDuplicatesMutation.isSuccess && (
          <p className="mt-2 text-sm text-green-700">✅ No similar issues found — this appears to be new.</p>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {['all', 'open', 'investigating', 'resolved', 'closed'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              statusFilter === status
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Issues List */}
      {isLoading ? (
        <p className="text-gray-500">Loading issues...</p>
      ) : issues.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {issues.map((issue: Issue) => (
            <div key={issue.id} className="p-4">
              {editingIssue?.id === issue.id ? (
                /* Edit Mode */
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">{issue.title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Root Cause</label>
                      <textarea
                        value={editingIssue.root_cause || ''}
                        onChange={(e) => setEditingIssue({ ...editingIssue, root_cause: e.target.value })}
                        rows={2}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Resolution</label>
                      <textarea
                        value={editingIssue.resolution || ''}
                        onChange={(e) => setEditingIssue({ ...editingIssue, resolution: e.target.value })}
                        rows={2}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                    <select
                      value={editingIssue.status}
                      onChange={(e) => setEditingIssue({ ...editingIssue, status: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="open">Open</option>
                      <option value="investigating">Investigating</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateIssueMutation.mutate({
                        id: editingIssue.id,
                        root_cause: editingIssue.root_cause,
                        resolution: editingIssue.resolution,
                        status: editingIssue.status,
                      })}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingIssue(null)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{issue.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                        {issue.status}
                      </span>
                    </div>
                    {issue.description && (
                      <p className="text-sm text-gray-600 mt-1">{issue.description}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                      <span className={getOccurrenceColor(issue.times_occurred)}>
                        Occurred {issue.times_occurred}x
                      </span>
                      {issue.category && <span>Category: {issue.category}</span>}
                      <span>First: {new Date(issue.first_seen_at).toLocaleDateString()}</span>
                      <span>Last: {new Date(issue.last_seen_at).toLocaleDateString()}</span>
                    </div>
                    {issue.root_cause && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">Root Cause:</span> {issue.root_cause}
                      </p>
                    )}
                    {issue.resolution && (
                      <p className="text-xs text-green-700 mt-1">
                        <span className="font-medium">Resolution:</span> {issue.resolution}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setEditingIssue(issue)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No issues in the register</p>
          <p className="text-sm text-gray-400 mt-1">Issues are automatically created when tickets are flagged</p>
        </div>
      )}
    </div>
  );
}

export default IssuesPage;
